import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';

import { apiService } from '@services/ApiService';
import { dateTimeService } from '@services/DateTimeService';
import { extensions } from '@services/Extensions';
import { type CategoryInfo } from '../models/CategoryInfo';
import { type AdvancedSearchResult } from '../models/SearchResult';
import { ImgShield } from '@components/Images/ImgShield';
import HighlightedText from '@components/HighlightedText';

import './_search-page.scss';

const untitledNoteTitle = 'Untitled note';

function getNoteStyle(color?: string | null): React.CSSProperties {
  if (!color || !color.match(/#[0-9A-Fa-f]{6}/g)) {
    return {};
  }
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);
  return {
    borderLeftColor: color,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.08)`
  };
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dateTimeService.format(date, 'DD MMM, YYYY');
}

const sourceLabel: Record<AdvancedSearchResult['source'], string> = {
  both: 'Both',
  keyword: 'Keyword',
  semantic: 'Semantic'
};

@observer
export default class SearchPage extends React.Component {
  @observable private _query = '';
  @observable private _matchAll = false;
  @observable private _isArchive = false;
  @observable private _categoryId = '';
  @observable.ref private _results: AdvancedSearchResult[] | null = null;
  @observable private _isLoading = false;
  @observable private _hasSearched = false;
  @observable private _semanticAvailable = false;
  @observable private _categories: CategoryInfo[] = [];

  private _cancellationSource: AbortController = new AbortController();

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    void this._loadConfig();
    void this._loadCategories();
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) {
      action(() => { this._query = q; })();
      this._performSearch();
    }
  }

  componentWillUnmount(): void {
    this._cancellationSource.abort();
  }

  private async _loadConfig() {
    const cfg = await apiService.get<{ qdrantConfigured: boolean; embeddingConfigured: boolean }>('/api/note/search-config');
    action(() => { this._semanticAvailable = cfg.qdrantConfigured && cfg.embeddingConfigured; })();
  }

  private async _loadCategories() {
    const categories = await apiService.get<CategoryInfo[]>('/api/note/category', null);
    action(() => { this._categories = categories; })();
  }

  private _onQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    action(() => { this._query = e.target.value; })();
    if (this._query.trim()) {
      extensions.executeTimeout(this._performSearch, 300);
    } else {
      this._cancel();
      action(() => { this._results = null; this._hasSearched = false; })();
    }
  };

  private _cancel() {
    this._cancellationSource.abort();
    this._cancellationSource = new AbortController();
  }

  private _performSearch = async () => {
    const text = this._query.trim();
    if (!text) return;
    this._cancel();
    action(() => { this._isLoading = true; this._hasSearched = true; })();

    const response = await apiService.post<{ results: AdvancedSearchResult[] }>('/api/note/search/advanced', {
      text,
      categoryId: this._categoryId || undefined,
      isArchive: this._isArchive,
      matchAll: this._matchAll
    }, { cancellationToken: this._cancellationSource.signal });

    action(() => {
      if (this._cancellationSource.signal.aborted) return;
      this._results = response.results;
      this._isLoading = false;
    })();
  };

  private _toggleMatchAll = () => {
    action(() => { this._matchAll = !this._matchAll; })();
    if (this._query.trim()) this._performSearch();
  };

  private _toggleArchive = () => {
    action(() => { this._isArchive = !this._isArchive; })();
    if (this._query.trim()) this._performSearch();
  };

  private _onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    action(() => { this._categoryId = e.target.value; })();
    if (this._query.trim()) this._performSearch();
  };

  private _openNote(noteId: string) {
    window.location.href = '/n/' + noteId;
  }

  private _renderResult(result: AdvancedSearchResult) {
    const terms = this._query.trim().split(/\s+/).filter(Boolean);
    return (
      <div
        key={result.noteId}
        className="search-result"
        style={getNoteStyle(result.color)}
        onClick={() => this._openNote(result.noteId)}
      >
        <div className="search-result-top">
          <div className="search-result-title">
            <span className="search-result-title-text"><HighlightedText text={result.name || untitledNoteTitle} terms={terms} /></span>
            {result.isProtected && <span className="search-result-protected"><ImgShield size={13} /></span>}
          </div>
          <div className="search-result-badges">
            <span className={`source-badge source-${result.source}`}>{sourceLabel[result.source]}</span>
            {result.source !== 'keyword' && result.semanticScore != null && (
              <span className="search-result-score">{result.semanticScore.toFixed(2)}</span>
            )}
          </div>
        </div>

        <div className="search-result-meta">
          {result.categoryName && (
            <>
              <span className="search-result-category">{result.categoryName}</span>
              <span className="search-result-sep">·</span>
            </>
          )}
          <span>{relativeTime(result.lastWrite)}</span>
          {result.isArchived && <span className="search-result-sep">· archived</span>}
        </div>

        {result.snippets.length > 0 && (
          <div className="search-result-snippets">
            {result.snippets.map((snippet, i) => (
              <span key={i} className="search-result-snippet"><HighlightedText text={snippet} terms={terms} /></span>
            ))}
          </div>
        )}
      </div>
    );
  }

  render(): JSX.Element {
    const showPlaceholder = !this._hasSearched && !this._isLoading;
    const showEmpty = this._hasSearched && !this._isLoading && this._results !== null && this._results.length === 0;

    return (
      <div className="search-page">
        <div className="search-page-inner">
          <h1 className="search-page-title">Search</h1>
          <p className="search-page-subtitle">
            Search across all your notes with keywords{this._semanticAvailable ? ' and semantic (embedding) matching' : ''}.
          </p>

          <div className="search-box">
            <span className="search-box-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              className="search-box-input"
              type="search"
              placeholder="Search notes by keyword or meaning…"
              value={this._query}
              onChange={this._onQueryChange}
              autoFocus
            />
          </div>

          <div className="search-filters">
            {this._categories.length > 0 && (
              <select className="filter-select" value={this._categoryId} onChange={this._onCategoryChange}>
                <option value="">All categories</option>
                {this._categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <label className={`filter-toggle${this._matchAll ? ' active' : ''}`}>
              <input type="checkbox" checked={this._matchAll} onChange={this._toggleMatchAll} />
              Match all words
            </label>
            <label className={`filter-toggle${this._isArchive ? ' active' : ''}`}>
              <input type="checkbox" checked={this._isArchive} onChange={this._toggleArchive} />
              Include archived
            </label>
          </div>

          {this._hasSearched && !this._isLoading && this._results !== null && (
            <div className="search-results-header">
              <span>{this._results.length} result{this._results.length === 1 ? '' : 's'}</span>
              {this._semanticAvailable && !this._results.some(r => r.source === 'semantic' || r.source === 'both') && (
                <span className="semantic-hint">No semantic matches (notes are indexed when edited)</span>
              )}
            </div>
          )}

          <div className="search-results">
            {showPlaceholder && (
              <div className="search-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <span>Start typing to search your notes.</span>
              </div>
            )}

            {this._isLoading && <div className="search-empty">Searching…</div>}

            {showEmpty && <div className="search-empty">No notes match your search.</div>}

            {this._results !== null && !this._isLoading && this._results.map(r => this._renderResult(r))}
          </div>
        </div>
      </div>
    );
  }
}
